import React, { useState } from "react";
import { Box, Button, Flex, Icon, Text } from "@chakra-ui/react";
import { HiChevronRight, HiOutlineArrowRight, HiOutlinePlus } from "react-icons/hi2";
import { c } from "../classroomColors";
import { Chip, inkButton, quietButton } from "./parts";
import { STATE, clock, dayLabel, duration, plural, questionsLine, serverTime } from "./spaceTokens";

// How many past sessions the board shows before pointing to the full list.
const PAST_SHOWN = 5;
const DRAG_TYPE = "text/stande-session";

/** "Today", "Tomorrow", or the date: how a timetable heads a day. */
const dayHeading = (value) => {
  const d = serverTime(value);
  if (!d) return null;
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(new Date())) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return dayLabel(value);
};

/** Scheduled sessions under the day each falls on, in order. */
const byDay = (sessions) => {
  const days = [];
  sessions.forEach((s) => {
    const label = dayHeading(s.scheduled_at);
    const last = days[days.length - 1];
    if (last && last.label === label) last.sessions.push(s);
    else days.push({ label, sessions: [s] });
  });
  return days;
};

/** How many came, out of the class: a count and a bar. */
const Turnout = ({ count, of, verb }) => (
  <Flex align="center" gap="10px">
    <Text fontSize="12px" color={c.textSub} flexShrink={0} sx={{ fontVariantNumeric: "tabular-nums" }}>
      {of ? `${count}/${of} ${verb}` : `${plural(count, "student")} ${verb}`}
    </Text>
    {of ? (
      <Box flex="1" h="5px" borderRadius="200px" bg={c.borderSoft} overflow="hidden">
        <Box h="100%" w={`${Math.min(100, Math.round((count / of) * 100))}%`} bg={STATE.SCHEDULED.dot} borderRadius="200px" />
      </Box>
    ) : null}
  </Flex>
);

/**
 * One session in a stack. The whole card opens its sheet; the buttons are
 * the next steps. A teacher can open the room of any session not yet over,
 * to get it ready or try it out: opening it does not start the session.
 */
const Card = ({ session, isInstructor, isSelected, studentCount, withDay, isNext, onOpen, onEnter, onSchedule, draggable }) => {
  const { state } = session;
  const isDone = state === "DONE";
  const isDraft = state === "DRAFT";
  // A session run without a date (straight from Drafts) is dated by when it went live.
  const at = session.scheduled_at || (isDone ? session.started_at : null);
  const when = [withDay ? dayLabel(at) : null, clock(at), duration(session.duration_minutes)]
    .filter(Boolean)
    .join(" · ");
  const extras = [
    session.activity_count ? plural(session.activity_count, "activity", "activities") : null,
    session.assignment_count ? plural(session.assignment_count, "task") : null,
    session.attachment_count ? plural(session.attachment_count, "file") : null,
  ].filter(Boolean);

  const openRoom = { label: "Open room", run: () => onEnter(session), arrow: true };
  const actions = isDone
    ? [{ label: "Recap", style: quietButton, run: () => onOpen(session) }]
    : !isInstructor
    ? []
    : isDraft
    ? [
        { label: "Schedule", style: quietButton, run: () => onSchedule(session) },
        { ...openRoom, style: quietButton },
      ]
    : [{ ...openRoom, style: isNext ? inkButton : quietButton }];

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-current={isSelected || undefined}
      onClick={() => onOpen(session)}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onOpen(session);
        }
      }}
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData(DRAG_TYPE, session.guid);
        e.dataTransfer.effectAllowed = "move";
      }}
      cursor={draggable ? "grab" : "pointer"}
      p="14px"
      borderRadius="14px"
      bg={c.page}
      border="1px solid"
      borderColor={isSelected ? c.text : c.borderSoft}
      boxShadow={isSelected ? `0 0 0 1px ${c.text}` : "none"}
      transition="border-color 0.15s"
      _hover={isSelected ? {} : { borderColor: c.textMuted }}
      _focusVisible={{ outline: "2px solid", outlineColor: STATE.SCHEDULED.dot, outlineOffset: "2px" }}
    >
      <Flex align="center" justify="space-between" gap="8px" mb="8px" minH="20px">
        <Text fontSize="12px" color={c.textSub} sx={{ fontVariantNumeric: "tabular-nums" }} noOfLines={1}>
          {isDraft ? ["Draft", duration(session.duration_minutes)].filter(Boolean).join(" · ") : when}
        </Text>
        {isInstructor && !isDone && session.pre_class_unanswered_count > 0 && (
          <Chip tone="blue">{session.pre_class_unanswered_count} to answer</Chip>
        )}
      </Flex>

      <Text fontSize="15px" fontWeight="600" color={c.text} lineHeight="1.3" letterSpacing="-0.005em" noOfLines={2}>
        {session.name}
      </Text>
      <Text fontSize="12px" color={c.textSub} mt="3px" noOfLines={1}>
        {isDone ? extras.join(" · ") || "Held" : questionsLine(session, isInstructor)}
      </Text>

      {isDone && session.attended_count > 0 && (
        <Box mt="10px">
          <Turnout count={session.attended_count} of={isInstructor ? studentCount : null} verb="came" />
        </Box>
      )}

      {actions.length > 0 && (
        <Flex gap="8px" mt="12px">
          {actions.map((a) => (
            <Button
              key={a.label}
              {...a.style}
              flex="1"
              h="36px"
              borderRadius="11px"
              fontSize="13px"
              rightIcon={a.arrow ? <Icon as={HiOutlineArrowRight} boxSize="13px" /> : undefined}
              onClick={(e) => {
                e.stopPropagation();
                a.run();
              }}
            >
              {a.label}
            </Button>
          ))}
        </Flex>
      )}
    </Box>
  );
};

/**
 * One stack: a white column standing on its own, its sessions stacked
 * inside. A stack that takes a dragged session marks itself while one is
 * held over it.
 */
const Stack = ({ title, count, drop, children }) => {
  const [over, setOver] = useState(false);
  return (
    <Flex
      direction="column"
      minW={{ base: "280px", lg: 0 }}
      minH={{ base: "320px", lg: "440px" }}
      p="18px"
      borderRadius="22px"
      bg={c.surface}
      border="1px"
      borderStyle={over ? "dashed" : "solid"}
      borderColor={over ? c.textMuted : c.borderSoft}
      transition="border-color 0.12s"
      scrollSnapAlign="start"
      onDragOver={
        drop
          ? (e) => {
              if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
              e.preventDefault();
              setOver(true);
            }
          : undefined
      }
      onDragLeave={drop ? () => setOver(false) : undefined}
      onDrop={
        drop
          ? (e) => {
              e.preventDefault();
              setOver(false);
              const guid = e.dataTransfer.getData(DRAG_TYPE);
              if (guid) drop.onDrop(guid);
            }
          : undefined
      }
    >
      <Flex align="baseline" gap="8px" px="2px" mb="14px">
        <Text fontSize="15px" fontWeight="500" color={c.text}>
          {title}
        </Text>
        <Text fontSize="12.5px" color={c.textMuted} sx={{ fontVariantNumeric: "tabular-nums" }}>
          {count}
        </Text>
        {over && (
          <Text ml="auto" fontSize="12px" color={c.textSub}>
            {drop.hint}
          </Text>
        )}
      </Flex>
      <Flex direction="column" gap="10px" flex="1">
        {children}
      </Flex>
    </Flex>
  );
};

const Empty = ({ children }) => (
  <Text fontSize="12.5px" color={c.textMuted} px="8px" py="14px" textAlign="center" lineHeight="1.5">
    {children}
  </Text>
);

/**
 * The space's sessions as three stacks, side by side and apart: drafts
 * (the teacher's), scheduled — under the day each falls on — and past
 * sessions. A live session isn't in a stack: it leads the page, above them.
 *
 * A teacher drags a draft onto Scheduled to give it a date, or a scheduled
 * session back to Drafts to take its date away.
 */
const SessionBoard = ({
  groups,
  isInstructor,
  selectedGuid,
  studentCount,
  onOpen,
  onEnter,
  onCreate,
  onSchedule,
  onUnschedule,
  onShowPast,
}) => {
  const find = (guid) => [...groups.drafts, ...groups.upcoming].find((s) => s.guid === guid);
  const cardProps = { isInstructor, studentCount, onOpen, onEnter, onSchedule };
  const past = groups.past.slice(0, PAST_SHOWN);
  const stacks = isInstructor ? 3 : 2;

  return (
    <Box overflowX={{ base: "auto", lg: "visible" }} mx={{ base: "-16px", md: 0 }} px={{ base: "16px", md: 0 }} pb="4px"
      sx={{ scrollSnapType: "x mandatory", scrollPaddingInline: "16px" }}>
      <Box
        display="grid"
        gridTemplateColumns={{ base: "none", lg: `repeat(${stacks}, minmax(0, 1fr))` }}
        gridAutoFlow={{ base: "column", lg: "row" }}
        gridAutoColumns={{ base: "max-content", lg: "unset" }}
        gap={{ base: "14px", lg: "28px" }}
        alignItems="stretch"
      >
        {isInstructor && (
          <Stack
            title="Drafts"
            count={groups.drafts.length}
            drop={{ hint: "Drop to take its date away", onDrop: (guid) => find(guid)?.state === "SCHEDULED" && onUnschedule(find(guid)) }}
          >
            <Flex
              as="button"
              type="button"
              onClick={onCreate}
              aria-label="New draft"
              title="New draft"
              align="center"
              justify="center"
              minH="96px"
              borderRadius="14px"
              border="1px solid"
              borderColor={c.borderSoft}
              bg={c.page}
              color={c.textSub}
              transition="border-color 0.15s, color 0.15s"
              _hover={{ color: c.text, borderColor: c.textMuted }}
            >
              <Icon as={HiOutlinePlus} boxSize="18px" />
            </Flex>
            {groups.drafts.map((s) => (
              <Card key={s.guid} session={s} isSelected={selectedGuid === s.guid} draggable {...cardProps} />
            ))}
          </Stack>
        )}

        <Stack
          title="Scheduled"
          count={groups.upcoming.length}
          drop={isInstructor ? { hint: "Drop to give it a date", onDrop: (guid) => find(guid)?.state === "DRAFT" && onSchedule(find(guid)) } : null}
        >
          {byDay(groups.upcoming).map((day) => (
            <Box key={day.label}>
              <Text fontSize="11px" fontWeight="600" letterSpacing="0.08em" textTransform="uppercase" color={c.textMuted} px="2px" mb="6px">
                {day.label}
              </Text>
              <Flex direction="column" gap="10px">
                {day.sessions.map((s) => (
                  <Card key={s.guid} session={s} isSelected={selectedGuid === s.guid} draggable={isInstructor}
                    isNext={s.guid === groups.upcoming[0]?.guid} {...cardProps} />
                ))}
              </Flex>
            </Box>
          ))}
          {!groups.upcoming.length && (
            <Empty>{isInstructor ? "Nothing scheduled. Drag a draft here to give it a date." : "Nothing coming up yet."}</Empty>
          )}
        </Stack>

        <Stack title="Past Sessions" count={groups.past.length}>
          {past.map((s) => (
            <Card key={s.guid} session={s} isSelected={selectedGuid === s.guid} withDay {...cardProps} />
          ))}
          {!past.length && <Empty>Sessions you've held land here, each with its recap.</Empty>}
          {groups.past.length > PAST_SHOWN && (
            <Flex as="button" type="button" onClick={onShowPast} align="center" justify="center" gap="4px" h="34px"
              fontSize="12.5px" color={c.textSub} _hover={{ color: c.text }}>
              All {groups.past.length} past sessions
              <Icon as={HiChevronRight} boxSize="13px" />
            </Flex>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default SessionBoard;
